import type { addFeedbackAttachmentBodyZod } from "@nlc/shared-types";
import type { z } from "zod";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Paperclip } from "lucide-react";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";

import type { CreateFeedbackFormData } from "@/features/request/feedback-tool/feedbackTool/hooks/useCreateFeedback.tsx";

import { Button } from "@/components/button";
import { toast } from "@/components/toast/toast-queue.ts";
import { Loading } from "@/features/request/feedback-tool/feedbackTool/components/Loading.tsx";
import { useFeedbackTool } from "@/features/request/feedback-tool/feedbackTool/hooks/useFeedbackTool.tsx";
import { useUploadAssets } from "@/features/request/feedback-tool/feedbackTool/hooks/useUploadAssets.tsx";
import { feedbackToolKeys } from "@/hooks/queryKeys.ts";
import { useUser } from "@/hooks/useUser.tsx";
import apiClient from "@/lib/api/client.ts";

export type CreateFeedbackAttachmentBody = z.infer<
  typeof addFeedbackAttachmentBodyZod
>;

interface UploadAssetProps {
  commentBatchId: string;
  isSelected: boolean;
  parentId: number;
}

export default function UploadAsset({
  commentBatchId,
  isSelected,
  parentId,
}: UploadAssetProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const formMethods = useForm<CreateFeedbackFormData>();
  const assets = formMethods.watch("assets") || [];

  const queryClient = useQueryClient();
  const { data } = useUser();
  const { formFeedBack, setFormFeedBack } = useFeedbackTool();

  const { handleFileChange } = useUploadAssets(
    "assets",
    assets,
    () => {},
    formMethods,
  );

  const isUploading = assets.some(
    (item) =>
      (item as { status: "error" | "success" | "uploading" }).status ===
      "uploading",
  );

  const isError = assets.some(
    (item) =>
      (item as { status: "error" | "success" | "uploading" }).status ===
      "error",
  );

  const upload = useMutation({
    mutationFn: async (data: CreateFeedbackAttachmentBody) =>
      apiClient.url(`/stories/feedback-tool/attachment`).post(data).json(),
    mutationKey: feedbackToolKeys.createFeedback(parentId),
    onError: () => {
      toast.add(
        {
          description: "Please try to create feedback again.",
          title: "Error uploading attachment",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    },
    onSuccess: async () => {
      toast.add(
        {
          title: "Attachment uploaded successfully",
          type: "success",
        },
        {
          timeout: 5000,
        },
      );

      setFormFeedBack((prevFormFeedBack) => {
        if (!prevFormFeedBack) return null;

        return {
          ...prevFormFeedBack,
          type: "EDIT",
        };
      });

      return await queryClient.invalidateQueries({
        queryKey: feedbackToolKeys.feedbacks(parentId),
      });
    },
  });

  useEffect(() => {
    if (assets.length === 1) {
      assets?.map((att, index) => {
        if (index === 0) {
          const status = (att as { status: "error" | "success" | "uploading" })
            .status;

          if (status === "success") {
            upload.mutate({
              attachmentId: Number(att.attachmentId),
              commentBatchId,
              name: (att as { file: File }).file.name,
              parentId,
              userId: data?.user?.id ?? 2,
            });

            return;
          }
        }
      });
    }
  }, [assets]);

  useEffect(() => {
    if (isError) {
      toast.add(
        {
          description: "Unsupported file extension.",
          title: "Unsupported Media Type",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    }
  }, [isError]);

  return isSelected && formFeedBack?.type === "EDIT" ? (
    <div className="flex justify-end">
      <Button
        className="bg-base w-15 mt-3"
        onPress={() => {
          inputRef.current?.click();
        }}
        variant="secondary"
      >
        {isUploading ? (
          <Loading />
        ) : (
          <>
            <input
              className="hidden"
              multiple
              onChange={handleFileChange}
              ref={inputRef}
              type="file"
            />
            <Paperclip
              className={`text-slate-600 cursor-pointer ${isSelected ? "opacity-100" : "opacity-50"}`}
              size={24}
            />
          </>
        )}
      </Button>
    </div>
  ) : null;
}
